import { Link } from "react-router-dom";
import {
  Search,
  CheckCircle,
  XCircle,
  FileText,
  Download,
  Eye,
  X,
  FileSearch,
  RefreshCw,
} from "lucide-react";
import { useState, useEffect } from "react";
import { applicationsAPI } from "../../services/api";

// Helper function to format date from YYYY-MM-DD to DD/M/YYYY
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function ApplicationsList({ filter }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [cardTypeFilter, setCardTypeFilter] = useState("all");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchApplications();
  }, [filter, cardTypeFilter, page, limit]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { page, limit };
      // Use correct filter parameter based on database schema
      // Filter by payment_status when filter is "pending"
      if (filter === "pending") {
        params.paymentStatus = "pending";
      }
      
      // Filter by cardType
      if (cardTypeFilter && cardTypeFilter !== "all") {
        params.cardType = cardTypeFilter;
      }
      
      const response = await applicationsAPI.getAll(params);
      
      if (response.success) {
        setApplications(response.data || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch applications");
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = applications.filter((app) => {
    const fullName = app.full_name || app.name || "";
    const email = app.email || "";
    const phone = app.phone || "";
    const cardType = app.card_type || app.cardType || "";
    
    const matchesSearch =
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phone.includes(searchTerm);
    const matchesCardType =
      cardTypeFilter === "all" || cardType === cardTypeFilter;
    const matchesFilter =
      filter === "pending" ? app.payment_status === "pending" : true;
    return matchesSearch && matchesCardType && matchesFilter;
  });

  const getDocumentBadge = (photoStatus, idProofStatus) => {
    const statuses = [];
    
    // Photo status
    switch (photoStatus) {
      case "submitted":
        statuses.push({ type: "Photo", status: "Submitted", className: "text-blue-600" });
        break;
      case "failed":
        statuses.push({ type: "Photo", status: "Failed", className: "text-red-600" });
        break;
      case "pending":
      default:
        statuses.push({ type: "Photo", status: "Pending", className: "text-yellow-600" });
    }
    
    // ID Proof status
    switch (idProofStatus) {
      case "submitted":
        statuses.push({ type: "ID", status: "Submitted", className: "text-blue-600" });
        break;
      case "failed":
        statuses.push({ type: "ID", status: "Failed", className: "text-red-600" });
        break;
      case "pending":
      default:
        statuses.push({ type: "ID", status: "Pending", className: "text-yellow-600" });
    }
    
    return statuses.map((item, index) => (
      <div key={index} className="text-xs">
        <span className="text-gray-500 mr-1">{item.type}:</span>
        <span className={item.className}>{item.status}</span>
      </div>
    ));
  };

  // Get overall document status based on both photo and ID proof
  const getOverallDocStatus = (photoStatus, idProofStatus) => {
    // If either is failed, overall is failed
    if (photoStatus === 'failed' || idProofStatus === 'failed') {
      return { status: 'Failed', className: 'text-red-600 bg-red-50' };
    }
    // Both must be submitted for overall to be submitted
    if (photoStatus === 'submitted' && idProofStatus === 'submitted') {
      return { status: 'Submitted', className: 'text-blue-600 bg-blue-50' };
    }
    // If either is not submitted (pending), overall is pending
    return { status: 'Pending', className: 'text-yellow-600 bg-yellow-50' };
  };

  const handleViewDocument = (docType, filePath) => {
    if (!filePath) return;
    setSelectedDocument({ type: docType, path: filePath });
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  const handleDownloadDocument = async (docType, filePath) => {
    if (!filePath) return;
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://backend.aifia.in';
      const response = await fetch(`${apiBaseUrl}/${filePath}`);
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to opening in new tab
      window.open(`${process.env.REACT_APP_API_BASE_URL || 'https://backend.aifia.in'}/${filePath}`, '_blank');
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">
            {filter === "pending" ? "Pending Applications" : "All Applications"}
          </h1>
          <p className="text-gray-600">Manage artist card applications</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#C46A1B] mx-auto mb-2" />
            <p className="text-gray-500">Loading applications...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">
            {filter === "pending" ? "Pending Applications" : "All Applications"}
          </h1>
          <p className="text-gray-600">Manage artist card applications</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchApplications}
            className="mt-2 px-4 py-2 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">
          {filter === "pending" ? "Pending Applications" : "All Applications"}
        </h1>
        <p className="text-gray-600">Manage artist card applications</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm flex flex-wrap gap-4">
        <div className="flex-1 flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
          <Search className="w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            className="bg-transparent border-none outline-none text-sm w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="px-4 py-2 border rounded-lg bg-white"
          value={cardTypeFilter}
          onChange={(e) => setCardTypeFilter(e.target.value)}
        >
          <option value="all">All Card Types</option>
          <option value="artist">Artist Card</option>
          <option value="senior">Senior Artist</option>
        </select>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-600">
                <th className="p-4">Date</th>
                <th className="p-4">Name</th>
                <th className="p-4">Card Type</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Documents</th>
                <th className="p-4">Payment</th>
                <th className="p-4">Details</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredApplications.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50">
                  <td className="p-4 text-sm whitespace-nowrap">
                    {formatDate(app.applied_date || app.appliedDate)}
                  </td>
                  <td className="p-4 font-medium whitespace-nowrap">
                    {app.full_name || app.name || "N/A"}
                  </td>
                  <td className="p-4 text-sm whitespace-nowrap">
                    {app.card_type || app.cardType || "N/A"}
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div>{app.phone || "N/A"}</div>
                      <div className="text-gray-500 text-xs">{app.email || ""}</div>
                    </div>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    {(() => {
                      const docStatus = getOverallDocStatus(app.photo_status, app.id_proof_status);
                      return (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${docStatus.className}`}>
                          {docStatus.status}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <span
                      className={`text-xs ${
                        app.payment_status === "completed" || app.payment === "completed"
                          ? "text-green-600"
                          : app.payment_status === "pending" || app.payment === "pending"
                            ? "text-yellow-600"
                            : "text-red-600"
                      }`}
                    >
                      {app.payment_status || app.payment || "pending"}
                    </span>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <Link to={`/applications/${app.id}`}>
                      <button className="flex items-center gap-1 text-[#C46A1B] hover:text-[#9E4E10] text-sm font-medium transition-colors">
                        <FileSearch className="w-4 h-4" />
                        <span>View</span>
                      </button>
                    </Link>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {/* View Photo */}
                      <button
                        onClick={() =>
                          handleViewDocument("Photo", app.photo_path)
                        }
                        className="p-1 hover:bg-blue-50 rounded transition-colors"
                        title="View Photo"
                      >
                        <Eye className="w-4 h-4 text-blue-600" />
                      </button>

                      {/* Download Photo */}
                      <button
                        onClick={() =>
                          handleDownloadDocument(
                            "Photo",
                            app.photo_path,
                          )
                        }
                        className="p-1 hover:bg-green-50 rounded transition-colors"
                        title="Download Photo"
                      >
                        <Download className="w-4 h-4 text-green-600" />
                      </button>

                      {/* View ID Proof */}
                      <button
                        onClick={() =>
                          handleViewDocument(
                            "ID Proof",
                            app.id_proof_path,
                          )
                        }
                        className="p-1 hover:bg-purple-50 rounded transition-colors"
                        title="View ID Proof"
                      >
                        <FileText className="w-4 h-4 text-purple-600" />
                      </button>

                      {/* Download ID Proof */}
                      <button
                        onClick={() =>
                          handleDownloadDocument(
                            "ID Proof",
                            app.id_proof_path,
                          )
                        }
                        className="p-1 hover:bg-orange-50 rounded transition-colors"
                        title="Download ID Proof"
                      >
                        <Download className="w-4 h-4 text-orange-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredApplications.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p>No applications found</p>
          </div>
        )}

        {/* Pagination */}
        <div className="p-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {filteredApplications.length} of {total} applications
          </p>
          <div className="flex gap-2">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button className="px-3 py-1 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]">
              {page}
            </button>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Document View Modal - Full screen overlay with inline styles */}
      {showDocumentModal && selectedDocument && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={handleCloseDocumentModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C46A1B]/10 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#C46A1B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#111111]">
                    {selectedDocument.type} Document
                  </h3>
                </div>
              </div>
              <button
                onClick={handleCloseDocumentModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body - Document Preview */}
            <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
              <div className="bg-gray-100 rounded-xl p-0 flex flex-col items-center justify-center min-h-[0px] border-2 border-dashed border-gray-300">
                {selectedDocument.path?.toLowerCase().includes(".pdf") ? (
                  <>
                    <FileText className="w-20 h-20 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-center mb-2">
                      PDF Document
                    </p>
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL || 'https://backend.aifia.in'}/${selectedDocument.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C46A1B] hover:underline"
                    >
                      Open PDF in new tab
                    </a>
                  </>
                ) : (
                  <>
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL || 'https://backend.aifia.in'}/${selectedDocument.path}`}
                      alt={`${selectedDocument.type} Preview`}
                      className="max-w-full max-h-[400px] object-contain rounded-lg shadow-md"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/400x300?text=No+Preview";
                      }}
                    />
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCloseDocumentModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDownloadDocument(
                      selectedDocument.type,
                      selectedDocument.path,
                    );
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C46A1B] text-white rounded-lg hover:bg-[#9E4E10] transition"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
